import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { logAction } from "./log.js";
import "../css/Login.css";

/**
 * Composant Login gérant la connexion à une dataroom via clé d'accès et
 * authentification multi-étapes avec email et code à 6 chiffres.
 *
 * Fonctionnalités principales :
 * - Récupération et validation de la clé dataroom depuis l'URL.
 * - Saisie et validation de l'email utilisateur.
 * - Envoi et vérification d'un code à 6 chiffres pour authentification.
 * - Gestion de la connexion directe via cookie "remember".
 * - Récupération et affichage du logo et du nom de l'office associé.
 * - Navigation vers la page d'accueil après connexion réussie.
 */
function Login() {
    // State pour stocker l'email saisi par l'utilisateur
    const [username, setUsername] = useState("");
    // Message d'erreur à afficher en cas de problème
    const [error, setError] = useState("");
    // Valeur saisie pour la clé dataroom (avant validation)
    const [keyInput, setKeyInput] = useState("");
    // Message de statut lié à la clé dataroom (ex: erreur ou succès)
    const [keyStatus, setKeyStatus] = useState("");
    // Booléen indiquant si la clé dataroom est valide
    const [isKeyValid, setIsKeyValid] = useState(false);
    // Contenu du logo récupéré (URL ou texte)
    const [logoContent, setLogoContent] = useState(null);
    // Nom de l'office récupéré et stocké en localStorage
    const [officeName, setOfficeName] = useState(localStorage.getItem('officeName') || "");

    // Booléen indiquant si on est à la deuxième étape (saisie du code)
    const [isStepTwo, setIsStepTwo] = useState(false);
    // Valeur saisie du code à 6 chiffres pour l'authentification
    const [codeInput, setCodeInput] = useState("");

    // URL de l'API, récupérée depuis la variable d'environnement
    const API_URL = process.env.REACT_APP_API_URL;

    // Hook de navigation pour rediriger l'utilisateur
    const navigate = useNavigate();
    // Hook pour accéder à la location actuelle (extraction des query params)
    const location = useLocation();

    // Référence pour l'input de la clé afin de lui donner le focus
    const keyInputRef = useRef(null);
    // Référence pour l'input du code afin de lui donner le focus
    const codeInputRef = useRef(null);

    /**
     * Récupère la valeur d'un cookie par son nom
     * @param {string} cookieName - Nom du cookie à récupérer
     * @returns {string|null} - Valeur du cookie ou null si absent
     */
    const getCookieValue = (cookieName) => {
        const cookies = document.cookie.split(';').map(cookie => cookie.trim());
        for (let cookie of cookies) {
            if (cookie.startsWith(`${cookieName}=`)) {
                return cookie.substring(cookieName.length + 1);
            }
        }
        return null;
    };

    /**
     * useEffect qui récupère la clé "key" dans l'URL,
     * puis valide cette clé auprès de l'API.
     * Si la clé est valide, on récupère et stocke le logo associé.
     */
    useEffect(() => {
        localStorage.clear();
        const params = new URLSearchParams(location.search);
        const keyValue = params.get("key");

        if (!keyValue) {
            setKeyStatus(" ");
            setIsKeyValid(false);
            return;
        }

        fetch(`${API_URL}/controllers/checkDataroom.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key: keyValue }),
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.success) {
                    setIsKeyValid(true);
                    setLogoContent(data.logo_content || null);
                } else {
                    setKeyStatus("Identifiant de la dataroom inconnu");
                    setIsKeyValid(false);
                    setLogoContent(null);
                }
            })
            .catch(() => {
                setKeyStatus("❌ Erreur serveur");
                setIsKeyValid(false);
                setLogoContent(null);
            });
    }, [location]);

    /**
     * Donne le focus à l'input de la clé lorsque celui-ci est visible.
     */
    useEffect(() => {
        // Si la clé n'est pas valide, l'input est affiché. On lui donne le focus.
        if (!isKeyValid && keyInputRef.current) {
            keyInputRef.current.focus();
        }
    }, [isKeyValid]);

    /**
     * Donne le focus à l'input du code lorsque celui-ci est visible (étape 2).
     */


    /**
     * Gestion de la soumission de la clé dataroom
     * saisie par l'utilisateur.
     * Redirige vers l'URL avec la clé en paramètre.
     */
    const handleKeySubmit = () => {
        if (keyInput.trim() === "") return;
        navigate(`/?key=${encodeURIComponent(keyInput.trim())}`);
    };

    /**
     * Fonction pour récupérer le nom de l'office
     * depuis l'API à partir de la clé client stockée.
     * Met à jour le state et le localStorage.
     */
    const fetchOfficeName = async () => {
        try {
            const response = await fetch(`${API_URL}/api/getOfficeName.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ key: localStorage.getItem('key') }),
            });

            const data = await response.json();

            console.log("Office name response:", data);

            if (data.officeName) {  // selon la structure de la réponse
                setOfficeName(data.officeName);
                localStorage.setItem('officeName', data.officeName);
            } else {
                console.error("Réponse inattendue lors de la récupération du nom de l'office");
            }
        } catch (error) {
            console.error("Erreur lors de la récupération du nom de l'office", error);
        }
    };

    /**
     * Fonction principale de gestion du login.
     * Selon la présence du cookie "remember", tente
     * une connexion directe ou lance la vérification
     * de l'email avec envoi du code.
     * @param {Event} e - événement submit du formulaire
     */
    const handleLogin = async (e) => {
        e.preventDefault();
        setError("");

        const keyValue = new URLSearchParams(location.search).get("key");
        if (!keyValue) {
            setError("Clé manquante");
            return;
        }

        const rememberCookieValue = getCookieValue('remember');

        const rememberResponse = await fetch(`${API_URL}/controllers/verifyRemember.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ remember: rememberCookieValue, email: username.toLowerCase() }),
        });

        const hasRememberCookie = await rememberResponse.json();

        console.log("Has remember cookie:", hasRememberCookie);

        if (hasRememberCookie.success === true) {
            // Connexion directe sans code
            try {
                const loginResponse = await fetch(`${API_URL}/controllers/login.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ email: username.toLowerCase(), key: keyValue, logo: logoContent, remember: hasRememberCookie }),
                });

                const loginData = await loginResponse.json();

                console.log("Login response:", loginData);

                if (loginData.success) {
                    localStorage.setItem('dossierId', loginData.dossier_id);
                    localStorage.setItem('key', loginData.clientKey);

                    logAction({
                        action: "connection",
                        dossierId: loginData.dossier_id,
                        email: loginData.email,
                        authorisation: loginData.partage,
                    });

                    fetchOfficeName(loginData.dossier_id);

                    navigate("/home");
                } else {
                    setError(loginData.message || "Erreur lors de la connexion");
                }
            } catch {
                setError("Erreur serveur");
            }
        } else {
            // Pas de cookie remember : commencer par vérifier l'email (envoi du code)
            try {
                const codeResponse = await fetch(`${API_URL}/controllers/verifyEmail.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ email: username.toLowerCase(), key: keyValue }),
                });

                const codeData = await codeResponse.json();

                if (codeData.success) {
                  setIsStepTwo(true); // passer à la saisie du code
                  if (isStepTwo && codeInputRef.current) {        
                    codeInputRef.current.focus();
                  }
                } else {
                    setError(codeData.message || "Erreur lors de la vérification de l'email.");
                }
            } catch {
                setError("Erreur serveur");
            }
        }
    };

    /**
     * Soumission du code à 6 chiffres envoyé par email
     * pour finaliser la connexion.
     * @param {Event} e - événement submit du formulaire
     */
    const handleCodeSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Validation basique du format du code
        if (!/^\d{6}$/.test(codeInput)) {
            setError("Veuillez entrer un code à 6 chiffres.");
            return;
        }

        const keyValue = new URLSearchParams(location.search).get("key");
        if (!keyValue) {
            setError("Clé manquante");
            return;
        }

        try {
            const loginResponse = await fetch(`${API_URL}/controllers/login.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email: username.toLowerCase(), key: keyValue, code: codeInput, logo: logoContent }),
            });

            const loginData = await loginResponse.json();

            if (loginData.success) {
                localStorage.setItem('dossierId', loginData.dossier_id);
                localStorage.setItem('key', loginData.clientKey);

                logAction({
                    action: "connection",
                    dossierId: loginData.dossier_id,
                    email: loginData.email,
                    authorisation: loginData.partage,
                });

                fetchOfficeName(loginData.dossier_id);

                navigate("/home");
            } else {
                setError(loginData.message || "Code incorrect ou erreur lors de la connexion");
            }
        } catch {
            setError("Erreur serveur");
        }
    };

    /**
     * Vérifie si une chaîne est une URL commençant par http ou https
     * @param {string} str - chaîne à tester
     * @returns {boolean} - true si c'est une URL valide
     */
    const isUrl = (str) => typeof str === "string" && /^https?:\/\//i.test(str);

    return (
        <div className="login-container">
            <div className="login-card">
                {!isKeyValid && <h2 className="login-header">Connexion</h2>}
                {keyStatus && <p className="login-key-status">{keyStatus}</p>}

                {/* Saisie de la clé dataroom si non valide */}
                {!isKeyValid && (
                    <>
                        <input
                            ref={keyInputRef}
                            type="text"
                            placeholder="Identifiant de la dataroom"
                            className="input w-full input-field- has-focus my-2"
                            value={keyInput}
                            onChange={(e) => setKeyInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleKeySubmit();
                            }}
                        />
                        <button className="btn btn-info w-full -login-button" onClick={handleKeySubmit}>
                            Se connecter
                        </button>
                    </>
                )}

                {/* Affichage du logo si présent */}
                {logoContent && (
                    isUrl(logoContent) ? (
                        <div className="logo-login-container">
                            <img
                                src={logoContent}
                                alt="Logo"
                                style={{ maxWidth: "300px", margin: "0 auto", marginBottom: "20px" }}
                            />
                        </div>
                    ) : (
                        <div className="logo-text" style={{marginBottom: "20px", whiteSpace: "pre-wrap"}}>
                            <p>{logoContent}</p>
                        </div>
                    )
                )}

                {/* Étape 1 : saisie email si clé valide et pas encore code */}
                {isKeyValid && !isStepTwo && (
                    <>
                        <p className="login-subtitle">
                            {officeName && <strong>{officeName}</strong>} <br />
                            Entrez votre adresse email pour accéder à la dataroom.
                        </p>

                        {error && <p className="login-error text-error">{error}</p>}

                        <input
                            ref={codeInputRef}
                            type="text"
                            placeholder="Adresse email"
                            className="input w-full my-2 input-field- step-two-focus"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleLogin(e);
                            }}
                        />
                        <button className="btn btn-info  w-full -login-button" onClick={handleLogin}>
                            Se connecter
                        </button>
                    </>
                )}

                {/* Étape 2 : saisie du code à 6 chiffres */}
                {isStepTwo && (
                    <>
                        <p className="login-subtitle">
                            Veuillez saisir le code à 6 chiffres que vous avez reçu.
                        </p>

                        {error && <p className="login-error">{error}</p>}

                        <input                            
                            type="text"
                            placeholder="Code à 6 chiffres"
                            className="input-field"
                            value={codeInput}
                            maxLength={6}
                            autocomplete="one-time-code"
                            onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, ""))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleCodeSubmit(e);
                            }}
                        />
                        <button className="btn btn-info  w-full -login-button" onClick={handleCodeSubmit}>
                            Valider le code
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default Login;
